import React, { useRef, useState } from "react";
import { ImageIcon, Loader2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { toast } from "sonner";

function apiError(err, fallback) {
  return err.response?.data?.message || fallback;
}

export default function ImageUploadField({
  value,
  onChange,
  branchId,
  label = "Image",
  hint = "Upload a photo or paste an image URL. Shown on POS when set.",
  testId = "image-upload",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const previewUrl = resolveMediaUrl(value);

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file || !branchId) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const { data } = await api.post("/menu-images", formData, {
        params: { branch_id: branchId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url || "");
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(apiError(err, "Image upload failed."));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div data-testid={testId}>
      {label ? <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label> : null}
      {hint ? <p className="text-[11px] text-slate-500 mb-3">{hint}</p> : null}
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <div className="h-28 w-28 rounded-lg border border-[#E5E7EB] bg-slate-50 overflow-hidden shrink-0">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full grid place-items-center text-slate-300">
              <ImageIcon size={28} strokeWidth={1.5} />
            </div>
          )}
        </div>
        <div className="flex-1 w-full min-w-0 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9"
              disabled={uploading || !branchId}
              onClick={() => inputRef.current?.click()}
            >
              {uploading ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <Upload size={14} className="mr-1.5" />
              )}
              {uploading ? "Uploading…" : "Upload image"}
            </Button>
            {value ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 text-red-600 hover:text-red-700"
                onClick={() => onChange("")}
              >
                <X size={14} className="mr-1" />
                Remove
              </Button>
            ) : null}
          </div>
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 text-[12px] w-full"
            placeholder="https://… or /storage/menu-images/…"
          />
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>
    </div>
  );
}
